<template>
  <div class="atlas-content-ingestion-panel">
    <input ref="folderInputRef" type="file" class="atlas-content-ingestion-panel__hidden-input" webkitdirectory directory multiple @change="onFolderSelected" />

    <AtlasIngestionStepper :currentStep="currentIngestionStep" :steps="ingestionSteps" />

    <div v-if="isProcessingSelection && hasPickedFolder" class="atlas-content-ingestion-panel__folder-bind-busy" role="status" aria-live="polite">
      <LoadingIcon :size="24" class="atlas-content-ingestion-panel__folder-bind-busy-icon" />
      <span class="atlas-content-ingestion-panel__folder-bind-busy-text">Scanning selected folder…</span>
    </div>

    <!-- 1. Choose folder step -->
    <section v-if="!hasPickedFolder" class="atlas-content-ingestion-panel__section atlas-content-ingestion-panel__section--select">
      <AtlasIngestionChooseFolderStep :is-processing-selection="isProcessingSelection" @chooseFolder="triggerSourcePicker" />
    </section>

    <!-- 2. Analyze step -->
    <AtlasIngestionAnalyzePanel
      v-if="hasPickedFolder && !hasAnalyzedGroups"
      :groupingMode="groupingMode"
      v-model:groupingGuidance="groupingGuidance"
      v-model:singleGroupName="singleGroupName"
      :folderLabel="folderLabel"
      :imageCount="manifestFiles.length"
      :excludedFileCount="excludedFileTotalCount"
      :isAnalyzing="isAnalyzing"
      :isUploading="isUploading"
      :canAnalyze="canAnalyzeSelection"
      :limitWarningMessage="analyzeLimitPanelWarning"
      :limitErrorMessage="analyzeLimitPanelError"
      @update:groupingMode="onGroupingModeUpdated"
      @changeFolder="triggerSourcePicker"
      @analyze="analyzeSelection"
      @restart="clearSession"
      @persist="persistSession"
    />

    <!-- 3. Review / upload step -->
    <AtlasIngestionUploadPanel
      v-if="hasPickedFolder && hasAnalyzedGroups"
      :groups="groups"
      :folderLabel="folderLabel"
      :showDebug="showDebug"
      :analysisNotes="analysisNotes"
      :isAnalyzing="isAnalyzing"
      :isUploading="isUploading"
      :uploadingStarted="uploadingStarted"
      :requiresFolderRebind="requiresFolderRebind"
      :allGroupsUploadComplete="allGroupsUploadComplete"
      :beginUploadButtonDisabled="beginUploadButtonDisabled"
      :beginUploadButtonLabel="beginUploadButtonLabel"
      :groupsSectionHeading="groupsSectionHeading"
      :libraryOverallPercent="libraryOverallPercent"
      :libraryUploadedImagesCount="uploadedImagesCount"
      :libraryCurrentGroupName="libraryCurrentGroupName"
      :libraryCurrentGroupUploadedCount="libraryCurrentGroupUploadedCount"
      :libraryCurrentGroupTotalCount="libraryCurrentGroupTotalCount"
      :libraryCompletedGroupsCount="libraryCompletedGroupsCount"
      :libraryTotalGroupsCount="libraryTotalGroupsCount"
      :librarySecondsRemainingApprox="librarySecondsRemainingDisplay"
      :limitErrorMessage="uploadLimitPanelError"
      :expandedGroupsById="expandedGroupsById"
      :returnTo="props.returnTo"
      :customerId="props.customerId"
      @beforeReturnNavigation="clearPersistedIngestionSession"
      @selectFolder="triggerSourcePicker"
      @uploadAll="uploadAllGroups"
      @back="goBackToAnalyze"
      @removeGroup="removeGroup"
      @removePrefixSource="removePrefixSourceFromGroup"
      @togglePreview="toggleGroupPreview"
      @renameGroup="renameGroupName"
      @mergeGroups="mergeGroups"
      :unmatchedGroups="unmatchedGroups"
      @promoteUnmatched="promoteUnmatchedToUploadGroup"
      @persist="persistSession"
    >
      <template #actionsTrailing>
        <SuperUserActionPanel>
          <div class="atlas-content-ingestion-panel__test-simulation-inner">
            <SrpCheckbox :isWithHalo="false">
              <template #input>
                <input type="checkbox" v-model="isTestSimulationEnabled" />
              </template>
              <template #text>
                <span>Simulate Uploads</span>
              </template>
            </SrpCheckbox>
            <SrpCheckbox :isWithHalo="false">
              <template #input>
                <input type="checkbox" v-model="showDebug" />
              </template>
              <template #text>
                <span>Show Debug</span>
              </template>
            </SrpCheckbox>
            <p v-if="libraryMbpsLabel" class="global-p atlas-content-ingestion-panel__test-mbps">{{ libraryMbpsLabel }}</p>
          </div>
        </SuperUserActionPanel>
      </template>
    </AtlasIngestionUploadPanel>
    <div v-if="errorMessage" class="atlas-content-ingestion-panel__notice atlas-content-ingestion-panel__notice--error">{{ errorMessage }}</div>
    <div v-if="successMessage" class="atlas-content-ingestion-panel__notice atlas-content-ingestion-panel__notice--success">{{ successMessage }}</div>
    <p v-if="analysisNotes && !groups.length && unmatchedGroups.length === 0" class="global-p atlas-content-ingestion-panel__small-note">{{ analysisNotes }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import AtlasIngestionStepper from "./AtlasIngestionStepper.vue";
import AtlasIngestionChooseFolderStep from "./AtlasIngestionChooseFolderStep.vue";
import AtlasIngestionAnalyzePanel from "./AtlasIngestionAnalyzePanel.vue";
import AtlasIngestionUploadPanel from "./AtlasIngestionUploadPanel.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import SrpCheckbox from "@components/ui/SrpCheckbox.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import type { AtlasIngestionEditableGroup } from "./AtlasIngestionGroupCard.vue";
import ContentIngestionRepo from "@repos/ContentIngestionRepo";
import { getCalendarId } from "@logic/calendarManager";
import { MAX_CONCURRENT_UPLOADS_DEFAULT, ParallelAssetUploader } from "@views/CMS/ParallelAssetUploader";
import { uploadAtlasPhotoWithRetry, validateAtlasPhotoFile } from "@logic/AtlasIngestionUpload";
import {
  backfillFolderPrefixesFromFilePaths,
  normalizeIngestionFolderPrefix,
  normalizeIngestionRelativePath,
  pathMatchesIngestionFilenamePrefix,
  pathMatchesIngestionFolderPrefix,
  resolveAtlasIngestionGroups,
} from "@logic/AtlasIngestionGrouping";
import { PhotoGallerySource } from "@contracts/photosPageData";
import {
  BulkImportSettingsResponse,
  ContentIngestionAnalyzeSummary,
  ContentIngestionGroupingMode,
  ContentIngestionManifestFileInput,
  ContentIngestionProposedGroup,
  SaveContentIngestionGroupRequest,
} from "@contracts/contentIngestion";
import { DEFAULT_BULK_UPLOAD_RETURN_TO, type BulkUploadReturnTo } from "./bulkUploadReturnTo";

defineOptions({
  name: "AtlasContentIngestionPanel",
});

const props = withDefaults(
  defineProps<{
    customerId: string;
    returnTo?: BulkUploadReturnTo;
  }>(),
  {
    returnTo: DEFAULT_BULK_UPLOAD_RETURN_TO,
  }
);

import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
const globalRemoteLogger = getGlobalRemoteLogger();

type EditableGroup = AtlasIngestionEditableGroup;

interface PersistedAtlasIngestionSession {
  schemaVersion: 2;
  groupingMode: ContentIngestionGroupingMode;
  groupingGuidance: string;
  singleGroupName: string;
  folderLabel: string;
  analysisSummary: ContentIngestionAnalyzeSummary | null;
  analysisNotes: string | null;
  appliedGroupingMode: ContentIngestionGroupingMode | null;
  bulkUploadSessionId: string | null;
  groups: Array<EditableGroup>;
  unmatchedGroups: Array<EditableGroup>;
  uploadedAssetIdsByPath: Record<string, string>;
  expandedGroupsById: Record<string, boolean>;
  successMessage: string;
}

const supportedClientExtensions = ref<Set<string>>(new Set());
const knownVideoExtensions = new Set(["mp4", "mov", "m4v", "webm", "avi", "wmv", "mkv", "mpeg", "mpg"]);
const serverMaxGroupSize = ref<number | null>(null);

/** Populated by loadBulkImportSettings; gates limit computeds until first successful fetch. */
const assetLimitsReady = ref(false);
/** Latest import-settings (includes manifest line/field separators for analyze payload). */
const bulkImportSettings = ref<BulkImportSettingsResponse | null>(null);
const currentUploadCount = ref(0);
const maxUploadsTotal = ref(0);
const maxUploadsPerSession = ref(2000);
const maxImagesToAnalyze = ref(15000);

const UPLOAD_THROUGHPUT_WINDOW_MS = 10_000;
const ETA_DISPLAY_THROTTLE_MS = 5000;
/** Minimum time between server `saveGroup` calls while a group is uploading (interim progress). */
const SAVE_GROUP_INTERVAL_MS = 10_000;

const ingestionSteps: Array<{ id: number; label: string }> = [
  { id: 1, label: "Select" },
  { id: 2, label: "Analyze" },
  { id: 3, label: "Review" },
  { id: 4, label: "Upload" },
];

const folderInputRef = ref<HTMLInputElement | null>(null);
const groupingMode = ref<ContentIngestionGroupingMode>("folder");
const groupingGuidance = ref("");
const singleGroupName = ref("");
const folderLabel = ref("");
const calendarId = ref("");
const isAnalyzing = ref(false);
const isUploading = ref(false);
/** True while parsing a folder selection on the main thread (large trees). */
const isProcessingSelection = ref(false);
/** Set when user clicks Begin Upload; used to lock down edit/skip and hide the "ready to upload" banner. */
const uploadFlowStarted = ref(false);
const manifestFiles = ref<Array<ContentIngestionManifestFileInput>>([]);
const analysisSummary = ref<ContentIngestionAnalyzeSummary | null>(null);
const groups = ref<Array<EditableGroup>>([]);
/** Client-side-only groups not queued for upload. */
const unmatchedGroups = ref<Array<EditableGroup>>([]);
const uploadedAssetIdsByPath = ref<Record<string, string>>({});
const selectedFilesByPath = shallowRef<Record<string, File>>({});
/** Maps each selected File to its relative path (O(1) lookup during upload). */
const pathByFile = ref(new WeakMap<File, string>());
const excludedVideoCount = ref(0);
const excludedOtherCount = ref(0);
const excludedEmptyCount = ref(0);
const expandedGroupsById = ref<Record<string, boolean>>({});
const errorMessage = ref("");
const successMessage = ref("");
const analysisNotes = ref<string | null>(null);
const appliedGroupingMode = ref<ContentIngestionGroupingMode | null>(null);
/** From Analyze response; persisted for save/complete after refresh. */
const bulkUploadSessionId = ref<string | null>(null);

/** Rolling window of completed file uploads (for ETA / Mbps). Cleared when a new upload run starts. */
const uploadThroughputHistory = ref<Array<{ tMs: number; bytes: number }>>([]);

// =====================================================================
// TEST-ONLY: simulation toggle. When enabled, uploadAtlasPhotoWithRetry
// is bypassed in favor of a randomized 200-800ms delay (see
// simulateUploadInsteadOfUploading below). All test-only code is
// confined to this block and the call-site marked "TEST-ONLY".
// =====================================================================
const isTestSimulationEnabled = ref(false);
const showDebug = ref(false);

async function simulateUploadInsteadOfUploading(): Promise<string> {
  const delayMs = 200 + Math.floor(Math.random() * 601);
  await new Promise(resolve => setTimeout(resolve, delayMs));
  return `test-sim-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
// =====================================================================
// END TEST-ONLY block
// =====================================================================

const requiresFolderRebind = computed(() => {
  if (!hasAnalyzedGroups.value) {
    return false;
  }
  const map = selectedFilesByPath.value;
  const uploaded = uploadedAssetIdsByPath.value;
  const pathsNeeded = new Set<string>();
  for (const g of groups.value) {
    for (const p of g.filePaths) {
      if (!uploaded[p]) {
        pathsNeeded.add(p);
      }
    }
  }
  for (const g of unmatchedGroups.value) {
    for (const p of g.filePaths) {
      if (!uploaded[p]) {
        pathsNeeded.add(p);
      }
    }
  }
  if (pathsNeeded.size === 0) {
    return false;
  }
  for (const p of pathsNeeded) {
    if (!map[p]) {
      return true;
    }
  }
  return false;
});

const uploadCap = computed(() => {
  if (!assetLimitsReady.value) {
    return Number.MAX_SAFE_INTEGER;
  }
  return Math.min(Math.max(0, maxUploadsTotal.value - currentUploadCount.value), maxUploadsPerSession.value);
});

function limitBindingWhenOverUploadCap(): "total" | "session" {
  const remaining = Math.max(0, maxUploadsTotal.value - currentUploadCount.value);
  return remaining <= maxUploadsPerSession.value ? "total" : "session";
}

type LimitBinding = "total" | "session" | null;

interface AnalyzeLimitStatus {
  kind: "ok" | "warn" | "blocked";
  message: ReadonlyArray<string> | null;
  binding: LimitBinding;
}

const analyzeLimitStatus = computed((): AnalyzeLimitStatus => {
  const n = manifestFiles.value.length;
  if (!assetLimitsReady.value || n === 0) {
    return { kind: "ok", message: null, binding: null };
  }
  const cap = uploadCap.value;
  const maxAnalyze = maxImagesToAnalyze.value;

  if (n > maxAnalyze) {
    return {
      kind: "blocked",
      message: [`The folder contains ${n.toLocaleString()} images, but we can only analyze ${maxAnalyze.toLocaleString()} at once.`, "Please choose a folder with fewer images."],
      binding: null,
    };
  }

  if (cap === 0 && n > 0) {
    return {
      kind: "blocked",
      message: ["Your gallery is at the maximum number of images. Please contact support@shrpa.com to increase your limit."],
      binding: "total",
    };
  }

  if (n > cap) {
    const binding = limitBindingWhenOverUploadCap();
    const msg: ReadonlyArray<string> =
      binding === "total"
        ? [
            `Your gallery allows ${maxUploadsTotal.value.toLocaleString()} images total and already has ${currentUploadCount.value.toLocaleString()}.`,
            `You can upload ${cap.toLocaleString()} more in this session.`,
          ]
        : [
            `The folder contains ${n.toLocaleString()} images, but only ${cap.toLocaleString()} can be uploaded at a time.`,
            `After analyzing, reduce the total to ${cap.toLocaleString()}, or choose a smaller folder.`,
          ];
    return { kind: "warn", message: msg, binding };
  }

  return { kind: "ok", message: null, binding: null };
});

const analyzeLimitPanelWarning = computed(() => (analyzeLimitStatus.value.kind === "warn" ? analyzeLimitStatus.value.message : null));
const analyzeLimitPanelError = computed(() => (analyzeLimitStatus.value.kind === "blocked" ? analyzeLimitStatus.value.message : null));

const canAnalyzeSelection = computed(() => !isAnalyzing.value && !isUploading.value && manifestFiles.value.length > 0 && analyzeLimitStatus.value.kind !== "blocked");
const totalImagesToUpload = computed(() => groups.value.reduce((sum, g) => sum + g.filePaths.length, 0));

const uploadLimitStatus = computed((): AnalyzeLimitStatus => {
  const n = totalImagesToUpload.value;
  if (!assetLimitsReady.value || n === 0) {
    return { kind: "ok", message: null, binding: null };
  }
  const cap = uploadCap.value;
  if (n <= cap) {
    return { kind: "ok", message: null, binding: null };
  }
  const binding = limitBindingWhenOverUploadCap();
  const msg: ReadonlyArray<string> =
    binding === "total"
      ? [
          `You have ${n.toLocaleString()} images ready for upload, but your gallery only has room for ${cap.toLocaleString()} more (limit ${maxUploadsTotal.value.toLocaleString()} total, ${currentUploadCount.value.toLocaleString()} already in use).`,
          `Remove one or more groups until the total is ${cap.toLocaleString()} or fewer.`,
        ]
      : [
          `You have ${n.toLocaleString()} images ready for upload, but you can only upload ${cap.toLocaleString()} per session.`,
          `Remove one or more groups until the total is ${cap.toLocaleString()} or fewer.`,
        ];
  return { kind: "blocked", message: msg, binding };
});

const uploadLimitPanelError = computed(() => (uploadLimitStatus.value.kind === "blocked" ? uploadLimitStatus.value.message : null));

const uploadedImagesCount = computed(() => {
  let n = 0;
  for (const g of groups.value) {
    for (const p of g.filePaths) {
      if (uploadedAssetIdsByPath.value[p]) {
        n += 1;
      }
    }
  }
  return n;
});
const libraryTotalGroupsCount = computed(() => groups.value.length);
const libraryCompletedGroupsCount = computed(() => groups.value.filter(g => g.status === "completed").length);

const libraryProgressTargetGroup = computed((): EditableGroup | null => {
  const uploading = groups.value.find(g => g.status === "uploading");
  if (uploading) {
    return uploading;
  }
  return groups.value.find(g => g.status === "pending" || g.status === "error") ?? null;
});

const libraryCurrentGroupName = computed(() => {
  const g = libraryProgressTargetGroup.value;
  if (!g) {
    return "—";
  }
  return (g.name || "").trim() || "Untitled group";
});

const libraryCurrentGroupUploadedCount = computed(() => {
  const g = libraryProgressTargetGroup.value;
  if (!g) {
    return 0;
  }
  const uniquePaths = [...new Set(g.filePaths)];
  return uniquePaths.filter(p => uploadedAssetIdsByPath.value[p]).length;
});

const libraryCurrentGroupTotalCount = computed(() => {
  const g = libraryProgressTargetGroup.value;
  if (!g) {
    return 0;
  }
  return [...new Set(g.filePaths)].length;
});

const libraryOverallPercent = computed(() => {
  const total = totalImagesToUpload.value;
  if (!total) {
    return 0;
  }
  return Math.min(100, Math.round((100 * uploadedImagesCount.value) / total));
});

const libraryThroughputStats = computed(() => {
  const history = uploadThroughputHistory.value;
  if (history.length < 2) {
    return { mbps: null as number | null, secondsRemaining: null as number | null };
  }
  const now = Date.now();
  const oldestSample = history[0];
  if (!oldestSample) {
    return { mbps: null as number | null, secondsRemaining: null as number | null };
  }
  const oldest = oldestSample.tMs;
  const spanSec = Math.max((now - oldest) / 1000, 0.5);
  const totalBytes = history.reduce((sum, h) => sum + h.bytes, 0);
  const bytesPerSec = totalBytes / spanSec;
  const mbps = (bytesPerSec * 8) / 1_000_000;
  const imagesInWindow = history.length;
  const imagesPerSec = imagesInWindow / spanSec;
  const remainingImages = Math.max(0, totalImagesToUpload.value - uploadedImagesCount.value);
  const secondsRemaining = imagesPerSec > 0 ? remainingImages / imagesPerSec : null;
  return { mbps, secondsRemaining };
});

const libraryMbpsApprox = computed(() => libraryThroughputStats.value.mbps);

const libraryMbpsLabel = computed(() => {
  const m = libraryMbpsApprox.value;
  if (m == null || !Number.isFinite(m) || m <= 0) {
    return null;
  }
  return `~${m.toFixed(1)} Mbps`;
});

const rawSecondsRemainingApprox = computed(() => libraryThroughputStats.value.secondsRemaining);
const librarySecondsRemainingDisplay = ref<number | null>(null);
let etaDisplayThrottleId: ReturnType<typeof setInterval> | null = null;

function syncLibrarySecondsRemainingDisplay() {
  librarySecondsRemainingDisplay.value = rawSecondsRemainingApprox.value;
}

watch(isUploading, uploading => {
  if (etaDisplayThrottleId != null) {
    clearInterval(etaDisplayThrottleId);
    etaDisplayThrottleId = null;
  }
  if (uploading) {
    syncLibrarySecondsRemainingDisplay();
    etaDisplayThrottleId = setInterval(syncLibrarySecondsRemainingDisplay, ETA_DISPLAY_THROTTLE_MS);
  } else {
    librarySecondsRemainingDisplay.value = null;
  }
});

onBeforeUnmount(() => {
  if (etaDisplayThrottleId != null) {
    clearInterval(etaDisplayThrottleId);
    etaDisplayThrottleId = null;
  }
});

const allGroupsUploadComplete = computed(() => groups.value.length > 0 && groups.value.every(g => g.status === "completed"));
/** True after upload has been started, or when every group is already completed (e.g. restored session). */
const uploadingStarted = computed(() => uploadFlowStarted.value || allGroupsUploadComplete.value);
const groupsSectionHeading = computed(() => {
  if (isUploading.value) {
    return `Uploading ${uploadedImagesCount.value} of ${totalImagesToUpload.value}`;
  }
  return `${groups.value.length} Upload Groups Proposed`;
});
const beginUploadButtonLabel = computed(() => {
  if (allGroupsUploadComplete.value) {
    return "Upload Finished!";
  }
  if (isUploading.value) {
    return "Uploading...";
  }
  return "Begin Upload";
});
const beginUploadButtonDisabled = computed(
  () => isAnalyzing.value || isUploading.value || groups.value.length === 0 || requiresFolderRebind.value || allGroupsUploadComplete.value || uploadLimitStatus.value.kind === "blocked"
);
const excludedFileTotalCount = computed(() => excludedVideoCount.value + excludedOtherCount.value + excludedEmptyCount.value);
const hasAnalyzedGroups = computed(() => groups.value.length > 0 || unmatchedGroups.value.length > 0);
const hasPickedFolder = computed(() => folderLabel.value.length > 0 || manifestFiles.value.length > 0 || excludedFileTotalCount.value > 0 || hasAnalyzedGroups.value);

const currentIngestionStep = computed(() => {
  if (!hasPickedFolder.value) {
    return 1;
  }
  if (!hasAnalyzedGroups.value) {
    return 2;
  }
  if (!uploadingStarted.value) {
    return 3;
  }
  return 4;
});

function relativePathFor(file: File): string {
  const withRelativePath = file as File & { webkitRelativePath?: string };
  const path = withRelativePath.webkitRelativePath && withRelativePath.webkitRelativePath.length > 0 ? withRelativePath.webkitRelativePath : file.name;
  return path.replace(/\\/g, "/").split("/").filter(Boolean).join("/");
}

function extensionOf(fileName: string): string {
  const last = fileName.split(".").pop();
  return last ? last.toLowerCase() : "";
}

function classifyExcludedFile(file: File, extensionFromFileName: string): "video" | "other" {
  if ((file.type || "").toLowerCase().startsWith("video/") || knownVideoExtensions.has(extensionFromFileName)) {
    return "video";
  }
  return "other";
}

function normalizeMode(rawMode: unknown): ContentIngestionGroupingMode {
  if (rawMode === "folder" || rawMode === "singleGroup" || rawMode === "jumbled") {
    return rawMode;
  }
  return "folder";
}

function storageKey(): string {
  return `atlas.contentIngestion.${props.customerId}.${calendarId.value}`;
}

/** Clears persisted ingestion session only (used when leaving after a successful full upload). */
function clearPersistedIngestionSession() {
  if (typeof window !== "undefined" && calendarId.value) {
    localStorage.removeItem(storageKey());
  }
}

function clearMessages() {
  errorMessage.value = "";
  successMessage.value = "";
}

function normalizeSupportedClientExtensions(raw: Array<string> | null | undefined): Set<string> {
  return new Set((raw ?? []).map(x => (x || "").trim().toLowerCase()).filter(Boolean));
}

async function loadBulkImportSettings(): Promise<BulkImportSettingsResponse> {
  const assetInfo = await ContentIngestionRepo.getBulkImportSettings(props.customerId);
  bulkImportSettings.value = assetInfo;
  currentUploadCount.value = assetInfo.currentUploadCount;
  maxUploadsTotal.value = assetInfo.maxUploadsTotal;
  maxUploadsPerSession.value = Number.isFinite(assetInfo.maxUploadsPerSession) ? Math.max(1, Math.round(assetInfo.maxUploadsPerSession)) : 2000;
  const rawMaxAnalyze = assetInfo.maxImagesToAnalyze;
  maxImagesToAnalyze.value =
    Number.isFinite(rawMaxAnalyze) && rawMaxAnalyze != null && rawMaxAnalyze > 0 ? Math.max(maxUploadsPerSession.value, Math.round(rawMaxAnalyze)) : Math.max(maxUploadsPerSession.value, 15000);
  assetLimitsReady.value = true;
  supportedClientExtensions.value = normalizeSupportedClientExtensions(assetInfo.supportedClientExtensions);
  const rawMaxGroupSize = assetInfo.maxGroupSize;
  serverMaxGroupSize.value = Number.isFinite(rawMaxGroupSize) ? Math.max(1, Math.round(rawMaxGroupSize)) : null;
  return assetInfo;
}

function toggleGroupPreview(groupId: string) {
  expandedGroupsById.value[groupId] = !expandedGroupsById.value[groupId];
  persistSession();
}

function renameGroupName(groupId: string, name: string) {
  const g = groups.value.find(x => x.groupId === groupId);
  if (g) {
    g.name = name;
  }
}

function makeUniqueRelativePath(relativePath: string, usedPaths: Set<string>): string {
  if (!usedPaths.has(relativePath)) {
    return relativePath;
  }

  const extension = extensionOf(relativePath);
  const suffixLength = extension ? extension.length + 1 : 0;
  const basePath = suffixLength > 0 ? relativePath.slice(0, -suffixLength) : relativePath;
  let counter = 2;
  let candidate = relativePath;
  while (usedPaths.has(candidate)) {
    candidate = extension ? `${basePath} (${counter}).${extension}` : `${basePath} (${counter})`;
    counter++;
  }
  return candidate;
}

function resetSelection() {
  folderLabel.value = "";
  manifestFiles.value = [];
  selectedFilesByPath.value = {};
  pathByFile.value = new WeakMap();
  excludedVideoCount.value = 0;
  excludedOtherCount.value = 0;
  excludedEmptyCount.value = 0;
}

function resetAnalysis() {
  analysisSummary.value = null;
  groups.value = [];
  unmatchedGroups.value = [];
  uploadedAssetIdsByPath.value = {};
  analysisNotes.value = null;
  expandedGroupsById.value = {};
  appliedGroupingMode.value = null;
  bulkUploadSessionId.value = null;
}

/** Manifest entries in arbitrary order; analyze step sorts before API call. */
function buildManifestFromFileMapUnsorted(fileMap: Record<string, File>): Array<ContentIngestionManifestFileInput> {
  return Object.entries(fileMap).map(([relativePath, file]) => ({
    relativePath,
    size: file.size,
  }));
}

/** Paths the server accepted for grouping, aligned with summary.acceptedFiles (sorted manifest, truncated). */
function acceptedRelativePathsForResolve(manifest: Array<ContentIngestionManifestFileInput>, summaryAcceptedCount: number): string[] {
  const sortedPaths = [...manifest].map(f => f.relativePath).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  const n = Math.min(summaryAcceptedCount, sortedPaths.length);
  return sortedPaths.slice(0, n);
}

/**
 * Checks if the server/AI returned a group whose explicit paths are not in the accepted manifest
 * slice and whose folder prefixes match nothing.
 * That group resolves to **zero** files. Showing it in the review step would be an empty card and
 * would confuse upload counts.
 *
 * This filter drops those empty resolved groups.
 */
function filterResolvedAtlasIngestionGroupsDroppingEmpty(
  resolved: Array<ContentIngestionProposedGroup>,
  proposedByGroupId: Map<string, ContentIngestionProposedGroup>,
  inputManifestCount: number,
  remoteLogger: { warn?: (message: string, immediate?: boolean) => void } | undefined
): Array<ContentIngestionProposedGroup> {
  return resolved.filter(g => {
    if (g.filePaths.length > 0) {
      return true;
    }
    const proposed = proposedByGroupId.get(g.groupId);
    const serverFilePaths = (proposed?.filePaths ?? []).join(", ");
    const folderPrefixesJoined = (g.folderPrefixes ?? []).join(", ");
    const filenamePrefixesJoined = (g.filenamePrefixes ?? []).join(", ");
    const message =
      `AtlasContentIngestion: dropping empty group "${g.name}" ` +
      `(folderPrefixes=[${folderPrefixesJoined}], filenamePrefixes=[${filenamePrefixesJoined}], serverFilePaths=[${serverFilePaths}], inputManifestCount=${inputManifestCount})`;
    console.warn(message);
    remoteLogger?.warn?.(message, true);
    return false;
  });
}

function buildSelectedFileMap(
  files: ArrayLike<File>,
  isDirectorySelection: boolean
): {
  map: Record<string, File>;
  pathByFile: WeakMap<File, string>;
  firstAcceptedRelativePath: string;
  excludedVideos: number;
  excludedOther: number;
  excludedEmpty: number;
} {
  const map: Record<string, File> = {};
  const pathByFileMap = new WeakMap<File, string>();
  const usedPaths = new Set<string>();
  let firstAcceptedRelativePath = "";
  let excludedVideos = 0;
  let excludedOther = 0;
  let excludedEmpty = 0;
  const shouldFilterBySupportedExtension = supportedClientExtensions.value.size > 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const basePath = isDirectorySelection ? relativePathFor(file) : file.name.replace(/\\/g, "/").split("/").filter(Boolean).join("/");
    if (!basePath) {
      continue;
    }

    const relativePath = makeUniqueRelativePath(basePath, usedPaths);
    const extension = extensionOf(file.name);
    if (file.size <= 0) {
      excludedEmpty++;
      usedPaths.add(relativePath);
      continue;
    }
    if (shouldFilterBySupportedExtension && !supportedClientExtensions.value.has(extension)) {
      const exclusionType = classifyExcludedFile(file, extension);
      if (exclusionType === "video") {
        excludedVideos++;
      } else {
        excludedOther++;
      }
      usedPaths.add(relativePath);
      continue;
    }

    map[relativePath] = file;
    pathByFileMap.set(file, relativePath);
    if (!firstAcceptedRelativePath) {
      firstAcceptedRelativePath = relativePath;
    }
    usedPaths.add(relativePath);
  }

  return {
    map,
    pathByFile: pathByFileMap,
    firstAcceptedRelativePath,
    excludedVideos,
    excludedOther,
    excludedEmpty,
  };
}

function handleFileSelection(files: ArrayLike<File> | null, isDirectorySelection: boolean, options?: { rebindOnly: boolean }) {
  const startTimeMs = Date.now();
  const logSelectionCompleted = (acceptedCount: number, filteredCount: number) => {
    const processingTimeMs = Date.now() - startTimeMs;
    globalRemoteLogger?.info?.(
      `BulkContentUpload: FolderSelectionProcessed: CustomerId=${props.customerId}, RebindOnly=${rebindOnly}, Accepted=${acceptedCount}, Filtered=${filteredCount}, ProcessingTimeMs=${processingTimeMs}`,
      false
    );
  };
  globalRemoteLogger?.info?.(`BulkContentUpload: FolderSelected: CustomerId=${props.customerId}, Files=${files?.length}, IsDirectorySelection=${isDirectorySelection}, Options=${options}`, false);
  const rebindOnly = options?.rebindOnly === true;
  if (!rebindOnly) {
    resetAnalysis();
  }
  if (!files?.length) {
    if (!rebindOnly) {
      resetSelection();
      persistSession();
    }
    logSelectionCompleted(0, 0);
    return;
  }

  const { map, pathByFile: newPathByFile, firstAcceptedRelativePath, excludedVideos, excludedOther, excludedEmpty } = buildSelectedFileMap(files, isDirectorySelection);
  const acceptedCount = Object.keys(map).length;
  const filteredCount = excludedVideos + excludedOther + excludedEmpty;
  pathByFile.value = newPathByFile;
  selectedFilesByPath.value = map;
  if (rebindOnly) {
    if (requiresFolderRebind.value) {
      errorMessage.value = "The selected folder does not match the saved upload session. Select the same root folder used for analysis.";
    }
    persistSession();
    logSelectionCompleted(acceptedCount, filteredCount);
    return;
  }

  manifestFiles.value = buildManifestFromFileMapUnsorted(map);
  excludedVideoCount.value = excludedVideos;
  excludedOtherCount.value = excludedOther;
  excludedEmptyCount.value = excludedEmpty;
  const firstPath = firstAcceptedRelativePath;
  const selectedFolderLabel = isDirectorySelection ? (firstPath && firstPath.includes("/") ? firstPath.split("/")[0] : firstPath) : `${manifestFiles.value.length} image files`;
  folderLabel.value = selectedFolderLabel;
  singleGroupName.value = selectedFolderLabel;
  persistSession();
  logSelectionCompleted(acceptedCount, filteredCount);
}

async function onFolderSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  const isRebindSelection = requiresFolderRebind.value && hasAnalyzedGroups.value;
  clearMessages();
  isProcessingSelection.value = true;
  await nextTick();
  await nextTick();
  await new Promise<void>(resolve => setTimeout(resolve, 0));
  try {
    handleFileSelection(files, true, { rebindOnly: isRebindSelection });
  } finally {
    isProcessingSelection.value = false;
    input.value = "";
  }
}

function triggerSourcePicker() {
  clearMessages();
  folderInputRef.value?.click();
}

/** User changed grouping mode from Analyze step; not used for session restore (avoids clearing restored groups). */
function onGroupingModeUpdated(mode: ContentIngestionGroupingMode) {
  if (groupingMode.value === mode) {
    return;
  }
  groupingMode.value = mode;
  if (mode !== "singleGroup") {
    singleGroupName.value = "";
  }
  clearMessages();
  resetAnalysis();
  persistSession();
}

async function analyzeSelection() {
  clearMessages();
  if (!manifestFiles.value.length) {
    errorMessage.value = "Choose a folder first.";
    return;
  }

  try {
    isAnalyzing.value = true;
    // await loadBulkImportSettings(); // only needed if we want to refresh in case it's stale

    const limitStatus = analyzeLimitStatus.value;
    if (limitStatus.kind === "blocked") {
      errorMessage.value = limitStatus.message?.join(" ") ?? "";
      if (manifestFiles.value.length > 0 && uploadCap.value === 0) {
        globalRemoteLogger?.warn?.(`--ALERT--AtlasContent: MAX LIMIT! CustomerId=${props.customerId}, manifestCount=${manifestFiles.value.length}, uploadCap=${uploadCap.value}`, true);
      }
      return;
    }

    const sortedManifest = [...manifestFiles.value].sort((a, b) => a.relativePath.localeCompare(b.relativePath, undefined, { sensitivity: "base" }));
    const manifestToAnalyze = sortedManifest;

    if (!bulkImportSettings.value) {
      await loadBulkImportSettings();
    }
    const settings = bulkImportSettings.value;
    if (!settings) {
      errorMessage.value = "Could not load import settings. Try again.";
      return;
    }

    const analysis = await ContentIngestionRepo.analyze(props.customerId, manifestToAnalyze, settings, {
      groupingMode: groupingMode.value,
      groupingGuidance: groupingGuidance.value.trim() || null,
      singleGroupName: groupingMode.value === "singleGroup" ? singleGroupName.value.trim() || null : null,
    });
    analysisSummary.value = analysis.summary;
    bulkUploadSessionId.value = analysis.bulkUploadSessionId ?? null;
    const proposedWithPrefixes = analysis.proposedGroups.map(g => ({
      ...g,
      folderPrefixes: g.folderPrefixes ?? [],
      filenamePrefixes: g.filenamePrefixes ?? [],
    }));
    const pathsForResolve = acceptedRelativePathsForResolve(manifestToAnalyze, analysis.summary.acceptedFiles);
    const resolved = resolveAtlasIngestionGroups(proposedWithPrefixes, pathsForResolve);
    const proposedByGroupId = new Map<string, ContentIngestionProposedGroup>(proposedWithPrefixes.map(g => [g.groupId, g]));
    const nonEmptyMatched = filterResolvedAtlasIngestionGroupsDroppingEmpty(resolved.matchedGroups, proposedByGroupId, manifestToAnalyze.length, globalRemoteLogger);
    groups.value = nonEmptyMatched.map(g => ({
      ...g,
      folderPrefixes: g.folderPrefixes ?? [],
      filenamePrefixes: g.filenamePrefixes ?? [],
      photoGalleryId: null,
      status: "pending",
      uploadPercent: 0,
      errorMessage: null,
      filePaths: [...g.filePaths].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    }));
    const u = resolved.unmatchedGroup;
    unmatchedGroups.value =
      u && u.filePaths.length > 0
        ? [
            {
              ...u,
              folderPrefixes: backfillFolderPrefixesFromFilePaths(u.filePaths),
              filenamePrefixes: u.filenamePrefixes ?? [],
              photoGalleryId: null,
              status: "pending",
              uploadPercent: 0,
              errorMessage: null,
              filePaths: [...u.filePaths].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
            },
          ]
        : [];
    uploadedAssetIdsByPath.value = {};
    analysisNotes.value = analysis.notes;
    appliedGroupingMode.value = normalizeMode(analysis.groupingModeApplied || groupingMode.value);
    // successMessage.value = `Analysis complete. ${analysis.summary.acceptedFiles} files accepted into ${analysis.summary.proposedGroups} groups.`;
    persistSession();
  } finally {
    isAnalyzing.value = false;
  }
}

function removeGroup(groupId: string) {
  const groupToRemove = groups.value.find(g => g.groupId === groupId);
  if (!groupToRemove) {
    return;
  }
  const excludedGroup: EditableGroup = {
    ...groupToRemove,
    status: "pending",
    uploadPercent: 0,
    errorMessage: null,
    photoGalleryId: null,
  };
  unmatchedGroups.value = [...unmatchedGroups.value, excludedGroup];
  groups.value = groups.value.filter(g => g.groupId !== groupId);
  delete expandedGroupsById.value[groupId];
  persistSession();
}

function removePrefixSourceFromGroup(groupId: string, sourceType: "folderPrefix" | "filenamePrefix", sourceValue: string) {
  const source = groups.value.find(g => g.groupId === groupId);
  if (!source) {
    return;
  }
  const prefix = normalizeIngestionFolderPrefix(sourceValue);
  if (!prefix) {
    return;
  }

  const matchesSourcePrefix = (relativePath: string) =>
    sourceType === "folderPrefix" ? pathMatchesIngestionFolderPrefix(relativePath, prefix) : pathMatchesIngestionFilenamePrefix(relativePath, prefix);
  const removedFiles = source.filePaths.filter(p => matchesSourcePrefix(normalizeIngestionRelativePath(p)));
  const remainingFiles = source.filePaths.filter(p => !matchesSourcePrefix(normalizeIngestionRelativePath(p)));
  const remainingFolders =
    sourceType === "folderPrefix" ? (source.folderPrefixes ?? []).filter(p => normalizeIngestionFolderPrefix(p) !== prefix) : source.folderPrefixes ?? [];
  const remainingFilenamePrefixes =
    sourceType === "filenamePrefix" ? (source.filenamePrefixes ?? []).filter(p => normalizeIngestionFolderPrefix(p) !== prefix) : source.filenamePrefixes ?? [];

  groups.value = groups.value.map(g =>
    g.groupId === groupId ? { ...g, folderPrefixes: remainingFolders, filenamePrefixes: remainingFilenamePrefixes, filePaths: remainingFiles } : g
  );

  if (removedFiles.length > 0) {
    unmatchedGroups.value = [
      ...unmatchedGroups.value,
      {
        ...source,
        groupId: `${source.groupId}__removed-${crypto.randomUUID()}`,
        name: sourceValue,
        folderPrefixes: sourceType === "folderPrefix" ? [sourceValue] : [],
        filenamePrefixes: sourceType === "filenamePrefix" ? [sourceValue] : [],
        filePaths: removedFiles,
        status: "pending",
        uploadPercent: 0,
        errorMessage: null,
        photoGalleryId: null,
      },
    ];
  }
  persistSession();
}

function promoteUnmatchedToUploadGroup(groupId: string) {
  const idx = unmatchedGroups.value.findIndex(g => g.groupId === groupId);
  if (idx < 0) {
    return;
  }
  const u = unmatchedGroups.value[idx];
  groups.value = [
    ...groups.value,
    {
      ...u,
      status: "pending",
      uploadPercent: 0,
      errorMessage: null,
      photoGalleryId: null,
    },
  ];
  unmatchedGroups.value = unmatchedGroups.value.filter(g => g.groupId !== groupId);
  persistSession();
}

function mergeGroups(groupIds: string[]) {
  if (groupIds.length < 2) {
    return;
  }
  const keysSet = new Set(groupIds);
  const selectedInOrder = groups.value.filter(g => keysSet.has(g.groupId));
  if (selectedInOrder.length < 2) {
    return;
  }

  const survivorId = selectedInOrder[0].groupId;
  const wasExpanded = expandedGroupsById.value[survivorId] === true;

  const merged: EditableGroup = {
    ...selectedInOrder[0],
    groupId: survivorId,
    name: selectedInOrder[0].name?.trim() || "Merged group",
    rationale: null,
    folderPrefixes: [...new Set(selectedInOrder.flatMap(g => g.folderPrefixes ?? []))],
    filenamePrefixes: [...new Set(selectedInOrder.flatMap(g => g.filenamePrefixes ?? []))],
    filePaths: [...new Set(selectedInOrder.flatMap(g => g.filePaths))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    photoGalleryId: null,
    status: "pending",
    uploadPercent: 0,
    errorMessage: null,
  };

  const firstIdx = groups.value.findIndex(g => g.groupId === selectedInOrder[0].groupId);
  let insertPos = 0;
  for (let j = 0; j < firstIdx; j++) {
    if (!keysSet.has(groups.value[j].groupId)) {
      insertPos++;
    }
  }

  const newGroups = groups.value.filter(g => !keysSet.has(g.groupId));
  newGroups.splice(insertPos, 0, merged);
  groups.value = newGroups;

  for (const k of groupIds) {
    delete expandedGroupsById.value[k];
  }
  if (wasExpanded) {
    expandedGroupsById.value[survivorId] = true;
  }

  persistSession();
}

function buildSaveRequest(group: EditableGroup): Omit<SaveContentIngestionGroupRequest, "clientSideDateTime"> {
  const assetIds = group.filePaths.map(path => uploadedAssetIdsByPath.value[path]).filter(Boolean);
  return {
    photoGalleryId: group.photoGalleryId,
    groupName: group.name?.trim() || "Atlas Group",
    assetIds,
    createdSource: PhotoGallerySource.CalendarManual,
    bulkUploadSessionId: bulkUploadSessionId.value,
  };
}

function resolveSelectedPath(file: File): string {
  return pathByFile.value.get(file) ?? relativePathFor(file);
}

async function saveGroupProgress(group: EditableGroup) {
  const response = await ContentIngestionRepo.saveGroup(props.customerId, group.groupId, buildSaveRequest(group));
  group.photoGalleryId = response.photoGalleryId;
}

function recordUploadThroughputSample(file: File) {
  const now = Date.now();
  const cutoff = now - UPLOAD_THROUGHPUT_WINDOW_MS;
  const pruned = uploadThroughputHistory.value.filter(s => s.tMs >= cutoff);
  uploadThroughputHistory.value = [...pruned, { tMs: now, bytes: file.size }];
}

async function uploadSingleGroup(group: EditableGroup) {
  const uniquePaths = [...new Set(group.filePaths)];
  const missingPaths = uniquePaths.filter(path => !selectedFilesByPath.value[path]);
  if (missingPaths.length > 0) {
    throw new Error(`Missing ${missingPaths.length} files for group '${group.name}'. Re-select the same selection before uploading.`);
  }

  group.status = "uploading";
  group.errorMessage = null;
  group.uploadPercent = 0;
  let saveChain = Promise.resolve();
  /** Start "in the present" so the first periodic save is not fired on the first completed file. */
  let lastSaveAtMs = Date.now();
  const uploader = new ParallelAssetUploader();
  const filesForGroup = uniquePaths.map(path => selectedFilesByPath.value[path]).filter(Boolean);

  await uploader.uploadImagesAndVideos(
    filesForGroup,
    async (file: File) => {
      const filePath = resolveSelectedPath(file);
      if (uploadedAssetIdsByPath.value[filePath]) {
        return;
      }

      const validation = validateAtlasPhotoFile(file);
      if (!validation.isValid) {
        throw new Error(validation.errorMessage || `Unsupported file '${file.name}'.`);
      }

      // TEST-ONLY: simulate uploads when the test checkbox is enabled. See isTestSimulationEnabled above.
      const uploadedAssetId = isTestSimulationEnabled.value ? await simulateUploadInsteadOfUploading() : await uploadAtlasPhotoWithRetry(file, props.customerId);
      uploadedAssetIdsByPath.value[filePath] = uploadedAssetId;
      recordUploadThroughputSample(file);
      // TEST-ONLY: suppress server-side group saves while simulating uploads.
      if (!isTestSimulationEnabled.value) {
        const nowMs = Date.now();
        if (nowMs - lastSaveAtMs >= SAVE_GROUP_INTERVAL_MS) {
          lastSaveAtMs = nowMs;
          saveChain = saveChain
            .then(() => saveGroupProgress(group))
            .catch((err: unknown) => {
              const msg = err instanceof Error ? err.message : String(err);
              globalRemoteLogger?.warn?.(`AtlasContentIngestion: periodic saveGroup failed (non-fatal): groupId=${group.groupId}, msg=${msg}`, false);
            });
        }
      }
      persistSession();
    },
    MAX_CONCURRENT_UPLOADS_DEFAULT,
    (_isActive: boolean, percent: number) => {
      group.uploadPercent = Math.round(percent);
    }
  );

  await saveChain;
  // TEST-ONLY: suppress completion API while simulating uploads.
  if (!isTestSimulationEnabled.value) {
    const completeResponse = await ContentIngestionRepo.completeGroup(props.customerId, group.groupId, buildSaveRequest(group));
    group.photoGalleryId = completeResponse.photoGalleryId;
  }
  group.status = "completed";
  group.uploadPercent = 100;
  group.errorMessage = null;
}

async function uploadAllGroups() {
  clearMessages();

  if (!groups.value.length) {
    errorMessage.value = "No groups to upload.";
    return;
  }
  if (requiresFolderRebind.value) {
    errorMessage.value = "Re-select the same files or folder before uploading.";
    return;
  }

  uploadFlowStarted.value = true;
  uploadThroughputHistory.value = [];
  const uploadBatchStartedAtMs = Date.now();
  try {
    isUploading.value = true;
    for (const group of groups.value) {
      if (group.status === "completed") {
        continue;
      }
      if (group.filePaths.length === 0) {
        group.status = "completed";
        continue;
      }

      try {
        await uploadSingleGroup(group);
      } catch (error: any) {
        group.status = "error";
        group.errorMessage = error?.message || "Upload failed for this group.";
        throw error;
      } finally {
        persistSession();
      }
    }

    // TEST-ONLY: suppress finalize API while simulating uploads.
    if (isTestSimulationEnabled.value) {
      successMessage.value = "Simulated upload complete. Group save/finalize skipped.";
    } else {
      const uploadDurationSeconds = (Date.now() - uploadBatchStartedAtMs) / 1000;
      const excludedGroups = unmatchedGroups.value.map(g => ({
        name: (g.name || "").trim() || "Untitled group",
        imageCount: [...new Set(g.filePaths ?? [])].length,
      }));
      await ContentIngestionRepo.finalize(props.customerId, bulkUploadSessionId.value, {
        uploadDurationSeconds,
        excludedGroups,
      });
      successMessage.value = "All groups uploaded and ingestion finalized.";
    }
    persistSession();
  } catch (error: any) {
    errorMessage.value = error?.message || "Upload flow failed.";
  } finally {
    isUploading.value = false;
  }
}

function persistSession() {
  if (!calendarId.value || typeof window === "undefined") {
    return;
  }
  if (!hasAnalyzedGroups.value) {
    localStorage.removeItem(storageKey());
    return;
  }
  const payload: PersistedAtlasIngestionSession = {
    schemaVersion: 2,
    groupingMode: groupingMode.value,
    groupingGuidance: groupingGuidance.value,
    singleGroupName: singleGroupName.value,
    folderLabel: folderLabel.value,
    analysisSummary: analysisSummary.value,
    analysisNotes: analysisNotes.value,
    appliedGroupingMode: appliedGroupingMode.value,
    bulkUploadSessionId: bulkUploadSessionId.value,
    groups: groups.value,
    unmatchedGroups: unmatchedGroups.value,
    uploadedAssetIdsByPath: uploadedAssetIdsByPath.value,
    expandedGroupsById: expandedGroupsById.value,
    successMessage: successMessage.value,
  };
  localStorage.setItem(storageKey(), JSON.stringify(payload));
}

function restoreSession() {
  if (!calendarId.value || typeof window === "undefined") {
    return;
  }
  const raw = localStorage.getItem(storageKey());
  if (!raw) {
    return;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) {
      localStorage.removeItem(storageKey());
      return;
    }
    const p = parsed as Partial<PersistedAtlasIngestionSession> & { manifestFiles?: unknown; notes?: string | null; schemaVersion?: number };
    if (p.schemaVersion !== 2 || Array.isArray(p.manifestFiles)) {
      localStorage.removeItem(storageKey());
      return;
    }
    groupingMode.value = normalizeMode(p.groupingMode);
    groupingGuidance.value = p.groupingGuidance || "";
    singleGroupName.value = p.singleGroupName || "";
    folderLabel.value = p.folderLabel || "";
    manifestFiles.value = [];
    excludedVideoCount.value = 0;
    excludedOtherCount.value = 0;
    excludedEmptyCount.value = 0;
    selectedFilesByPath.value = {};
    pathByFile.value = new WeakMap();
    analysisSummary.value = p.analysisSummary
      ? {
          totalFiles: p.analysisSummary.totalFiles ?? 0,
          acceptedFiles: p.analysisSummary.acceptedFiles ?? 0,
          rejectedFiles: p.analysisSummary.rejectedFiles ?? 0,
          proposedGroups: p.analysisSummary.proposedGroups ?? 0,
        }
      : null;
    const mapStoredGroup = (g: EditableGroup & { groupKey?: string }): EditableGroup => ({
      ...g,
      groupId: g.groupId || g.groupKey || crypto.randomUUID(),
      folderPrefixes: g.folderPrefixes ?? [],
      filenamePrefixes: g.filenamePrefixes ?? [],
      photoGalleryId: g.photoGalleryId || null,
      status: g.status || "pending",
      uploadPercent: g.uploadPercent || 0,
      errorMessage: g.errorMessage || null,
    });
    const restoredGroups = (p.groups || []).map(mapStoredGroup);
    let restoredUnmatchedGroups: EditableGroup[] = (p.unmatchedGroups ?? []).map(mapStoredGroup);
    restoredUnmatchedGroups = restoredUnmatchedGroups.map(group => {
      if ((group.folderPrefixes ?? []).length > 0) {
        return group;
      }
      return {
        ...group,
        folderPrefixes: backfillFolderPrefixesFromFilePaths(group.filePaths),
      };
    });
    if (restoredGroups.length === 0 && restoredUnmatchedGroups.length === 0) {
      localStorage.removeItem(storageKey());
      return;
    }
    groups.value = restoredGroups;
    unmatchedGroups.value = restoredUnmatchedGroups;
    uploadedAssetIdsByPath.value = p.uploadedAssetIdsByPath || {};
    expandedGroupsById.value = p.expandedGroupsById ?? (p as { expandedGroupsByKey?: Record<string, boolean> }).expandedGroupsByKey ?? {};
    analysisNotes.value = p.analysisNotes ?? p.notes ?? null;
    successMessage.value = p.successMessage || "";
    appliedGroupingMode.value = p.appliedGroupingMode ? normalizeMode(p.appliedGroupingMode) : null;
    bulkUploadSessionId.value = p.bulkUploadSessionId ?? null;
  } catch {
    localStorage.removeItem(storageKey());
  }
}

function clearSession() {
  uploadFlowStarted.value = false;
  uploadThroughputHistory.value = [];
  groupingMode.value = "folder";
  groupingGuidance.value = "";
  singleGroupName.value = "";
  resetSelection();
  resetAnalysis();
  clearMessages();
  if (typeof window !== "undefined" && calendarId.value) {
    localStorage.removeItem(storageKey());
  }
}

function goBackToAnalyze() {
  if (isUploading.value || isAnalyzing.value) {
    return;
  }
  uploadFlowStarted.value = false;
  uploadThroughputHistory.value = [];
  resetAnalysis();
  clearMessages();
  persistSession();
}

onMounted(async () => {
  calendarId.value = await getCalendarId(props.customerId);
  await loadBulkImportSettings();
  restoreSession();
});
</script>

<style scoped lang="scss">
.atlas-content-ingestion-panel {
  position: relative;

  &__folder-bind-busy {
    margin-top: 16px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border-radius: 8px;
    background: rgba(17, 134, 137, 0.08);
    border: 1px solid rgba(17, 134, 137, 0.25);
    font-size: 14px;
    color: #1a1a1a;
  }

  &__folder-bind-busy-icon {
    flex-shrink: 0;
  }

  &__folder-bind-busy-text {
    line-height: 1.35;
  }

  &__hidden-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }

  &__section {
    margin-top: 20px;
  }

  &__section--select {
    margin-top: 0;
  }

  &__notice {
    margin-top: 14px;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;

    &--warning {
      background: rgba(255, 193, 7, 0.16);
    }

    &--error {
      background: rgba(220, 53, 69, 0.16);
    }

    &--success {
      background: rgba(40, 167, 69, 0.16);
    }
  }

  &__small-note {
    margin-top: 8px;
    color: var(--srp-color-text-muted, #666);
    font-size: 13px;
  }

  // TEST-ONLY: simulation controls live in upload panel action row (slot); keep inner layout here.
  &__test-simulation-inner {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
  }

  &__test-mbps {
    margin: 0;
    font-size: 12px;
    line-height: 1.3;
    color: var(--srp-color-text-muted, #666);
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum";
  }
}
</style>
